export type EventLifecycleState =
  | 'draft'
  | 'active'
  | 'retention_expiring'
  | 'archived'
  | 'storage_subscription_active'
  | 'storage_expired'
  | 'scheduled_for_deletion'
  | 'deleted';

export type EventLifecycleInput = {
  lifecycle_state?: EventLifecycleState | string | null;
  lifecycle_state_changed_at?: string | null;
  lifecycle_state_reason?: string | null;
  deleted_at?: string | null;
  archived_at?: string | null;
  storage_status?: string | null;
  storage_subscription_active?: boolean | null;
  is_active?: boolean | null;
  retention_until?: string | null;
  expires_at?: string | null;
  uploads_locked?: boolean | null;
  is_upload_locked?: boolean | null;
};

// Browser-safe mirror of src/utils/eventLifecycle.ts on main.
// Keep this file aligned with the mobile source-of-truth contract; do not invent
// web-only lifecycle states or backend behavior here.
export function getEventLifecycleState(event?: EventLifecycleInput | null): EventLifecycleState {
  const state = String(event?.lifecycle_state ?? '').trim() as EventLifecycleState;
  if (state) return state;

  if (event?.deleted_at || event?.storage_status === 'deleted') return 'deleted';
  if (event?.archived_at) return 'archived';
  if (event?.storage_subscription_active) return 'storage_subscription_active';
  if (event?.storage_status === 'expired') return 'storage_expired';
  if (event?.is_active === false) return 'archived';
  return 'active';
}

export function isEventDeleted(event?: EventLifecycleInput | null): boolean {
  return getEventLifecycleState(event) === 'deleted';
}

function retentionEndsInFuture(event?: EventLifecycleInput | null): boolean {
  const value = event?.retention_until || event?.expires_at || null;
  if (!value) return false;
  const date = new Date(value);
  return !Number.isNaN(date.getTime()) && date.getTime() > Date.now();
}

export function eventAllowsUploads(event?: EventLifecycleInput | null): boolean {
  if (!event || isEventDeleted(event)) return false;
  if (event.is_upload_locked || event.uploads_locked) return false;

  const lifecycleState = getEventLifecycleState(event);
  if (['active', 'storage_subscription_active'].includes(lifecycleState)) return true;

  const storageStatus = String(event.storage_status ?? '').trim();
  return (
    lifecycleState === 'retention_expiring' &&
    storageStatus === 'ending_soon' &&
    retentionEndsInFuture(event)
  );
}

export function eventAllowsGuestExpansion(event?: EventLifecycleInput | null): boolean {
  if (!event || isEventDeleted(event)) return false;
  return ['draft', 'active', 'storage_subscription_active'].includes(getEventLifecycleState(event));
}

export function eventIsViewOnly(event?: EventLifecycleInput | null): boolean {
  const lifecycleState = getEventLifecycleState(event);
  const storageStatus = String(event?.storage_status ?? '').trim();
  if (
    lifecycleState === 'retention_expiring' &&
    storageStatus === 'ending_soon' &&
    retentionEndsInFuture(event)
  ) {
    return false;
  }
  return ['retention_expiring', 'archived', 'storage_expired', 'scheduled_for_deletion'].includes(
    lifecycleState,
  );
}

export function shouldShowEventInWebList(event?: EventLifecycleInput | null): boolean {
  if (!event) return false;
  const state = getEventLifecycleState(event);
  return state !== 'deleted' && state !== 'scheduled_for_deletion';
}

export function getEventLifecycleLabel(event?: EventLifecycleInput | null): string {
  switch (getEventLifecycleState(event)) {
    case 'draft': return 'Draft';
    case 'active': return 'Active';
    case 'retention_expiring': return 'Retention ending';
    case 'archived': return 'Archived';
    case 'storage_subscription_active': return 'Storage active';
    case 'storage_expired': return 'Storage expired';
    case 'scheduled_for_deletion': return 'Scheduled for deletion';
    case 'deleted': return 'Deleted';
    default: return 'Unknown';
  }
}

export function getEventLifecycleMessage(event?: EventLifecycleInput | null): string | null {
  switch (getEventLifecycleState(event)) {
    case 'draft': return 'This event is in draft mode until an event package is activated.';
    case 'active': return null;
    case 'retention_expiring': return 'This event is approaching its included storage period end.';
    case 'archived': return 'This album is archived. Uploads and new guest access are disabled.';
    case 'storage_subscription_active': return 'Account storage is active.';
    case 'storage_expired': return 'Storage access has expired. Uploads and new guest access are disabled.';
    case 'scheduled_for_deletion': return 'This album is scheduled for deletion.';
    case 'deleted': return 'This album has been deleted.';
    default: return null;
  }
}
